import { Checkbox, GridIcon, Input, Select, SelectOptionType } from '@bp/ui-components';
import { useSortable } from '@dnd-kit/sortable';
import classNames from 'classnames';
import { Field, FieldArrayRenderProps, useFormikContext } from 'formik';
import { ChangeEvent, CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';
import { SubstitutionPlanColumn, SubstitutionPlanFormType } from './SubstitutionPlanForm';
import styles from './SubstitutionPlanFormRow.module.scss';

type SubstitutionPlanFormRowProps = {
  index: number;
  helper: FieldArrayRenderProps;
  readonly: boolean;
  onChange: (e: React.ChangeEvent<unknown>) => void;
};

export const SubstitutionPlanFormRow = ({ index, helper, readonly, onChange }: SubstitutionPlanFormRowProps) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<SubstitutionPlanFormType>();

  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: `row-${index}`,
  });

  const dragStyle: CSSProperties | undefined = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        transition,
      }
    : undefined;

  const { order, name, print, width, sortOrder, ascending }: SubstitutionPlanColumn = {
    id: values.columns[index].column,
    column: values.columns[index].column,
    order: values.columns[index].order,
    name: values.columns[index].name,
    print: values.columns[index].print,
    width: values.columns[index].width,
    sortOrder: values.columns[index].sortOrder,
    ascending: values.columns[index].ascending,
  };

  const usedSorts = values.columns.map((c) => c.sortOrder);

  const sortOptions: SelectOptionType[] = [
    {
      label: '1',
      value: 1,
      disabled: usedSorts.includes(1),
    },
    {
      label: '2',
      value: 2,
      disabled: usedSorts.includes(2) || !usedSorts.includes(1),
    },
    {
      label: '3',
      value: 3,
      disabled: usedSorts.includes(3) || !usedSorts.includes(1) || !usedSorts.includes(2),
    },
  ];

  const directionOptions: SelectOptionType[] = [
    {
      label: t('common.descending'),
      value: 'descending',
    },
    {
      label: t('common.ascending'),
      value: 'ascending',
    },
  ];

  return (
    <div
      className={classNames(styles['substitution-plan-form-row'], { [styles.dragging]: isDragging })}
      ref={setNodeRef}
      style={dragStyle}
    >
      {!readonly && (
        <button className={styles.handler} ref={setActivatorNodeRef} {...listeners} {...attributes}>
          <GridIcon />
        </button>
      )}
      <Field
        className={styles.index}
        as={Input}
        name={readonly ? `${helper.name}.${index}.order-readonly` : `${helper.name}.${index}.order`}
        value={order}
        dense
        readonly
      />
      <div className={classNames(styles.name, { [styles.disabled]: !print })}>{name}</div>
      <Field
        className={styles.print}
        as={Checkbox}
        name={readonly ? `${helper.name}.${index}.print-readonly` : `${helper.name}.${index}.print`}
        checked={print}
        onChange={async (e: ChangeEvent<HTMLInputElement>) => {
          const checked = e.target.checked;
          await setFieldValue(`${helper.name}.${index}.print`, checked);
          if (!checked) {
            await setFieldValue(`${helper.name}.${index}.sortOrder`, 0);
          }
        }}
        readonly={readonly}
      />
      <Field
        className={styles.width}
        as={Input}
        name={readonly ? `${helper.name}.${index}.width-readonly` : `${helper.name}.${index}.width`}
        value={print && width ? Math.round((width + Number.EPSILON) * 100) / 100 : undefined}
        onChange={onChange}
        dense
        suffix='%'
        readonly={readonly}
        disabled={!readonly && !print}
        type='number'
        min={0}
        max={100}
        step={0.01}
      />
      <Field
        className={styles.order}
        as={Select}
        name={readonly ? `${helper.name}.${index}.sortOrder-readonly` : `${helper.name}.${index}.sortOrder`}
        options={sortOptions}
        value={print && sortOptions.find((o) => o.value === sortOrder)}
        onChange={async (option: SingleValue<SelectOptionType>) => {
          const value = (option as SingleValue<SelectOptionType>)?.value;
          await setFieldValue(`${helper.name}.${index}.sortOrder`, value);
        }}
        dense
        isClearable
        readonly={readonly}
        disabled={!print}
      />
      <Field
        className={styles.direction}
        as={Select}
        name={readonly ? `${helper.name}.${index}.ascending-readonly` : `${helper.name}.${index}.ascending`}
        options={directionOptions}
        value={print && sortOrder ? directionOptions[ascending ? 1 : 0] : undefined}
        onChange={async (option: SingleValue<SelectOptionType>) => {
          const opt = option?.value === 'ascending';
          await setFieldValue(`${helper.name}.${index}.ascending`, opt);
        }}
        dense
        readonly={readonly}
        disabled={!print || !sortOrder}
      />
    </div>
  );
};
